import React, { useEffect, useState } from "react";
import DrawerForm from "./DrawerForm";
import { Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import Button from "./ui/Button";
import { createRole, fetchRoleById, updateRole, type CreateRolePayload, type UpdateRolePayload } from "../services/groupsService";
import { formatPermission } from "../lib/utils";

export interface GroupDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  groupId?: string | null; // if set -> edit mode, otherwise create
  onSaved?: () => void;
}

// Permission Roles for better organization
const PERMISSION_GROUPS = {
  management: {
    title: "Management Permissions",
    permissions: [
      "Manage_Users",
      "Manage_Roles",
    ]
  },
  codesheets: {
    title: "Codesheets Permissions",
    permissions: [
      "View_Codesheets",
      "Create_Codesheets",
      "Update_Codesheets",
      "Delete_Codesheets",
      
      // "Version_Codesheets",
      "Comment_Codesheets",
      // "Assign_Collaborators",
      // "Remove_Collaborators"
    ]
  },
  changes: {
    title: "Changes & Versioning Permissions",
    permissions: [
      "Publish_Codesheets",
      "Submit_Changeset",
      // "Create_Changeset",
      // "Update_Changeset",
      // "Delete_Changeset",
      "Review_Changeset",
      "Merge_Changeset",
      "View_Version_History",
      // "Compare_Versions",
      "Rollback_Version"
    ]
  }
};

const LEGACY_PERMISSION_ALIASES: Record<string, string> = {
  // Versioning_Codesheets: "Version_Codesheets",
};

const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  Create_Codesheets: ["View_Codesheets"],
  Update_Codesheets: ["View_Codesheets"],
  Delete_Codesheets: ["Update_Codesheets"],
  // Publish_Codesheets: ["Update_Codesheets"],
  // Version_Codesheets: ["Update_Codesheets"],
  Comment_Codesheets: ["View_Codesheets"],
  // Assign_Collaborators: ["Update_Codesheets"],
  // Remove_Collaborators: ["Update_Codesheets"],
  // Create_Changeset: ["Update_Codesheets"],
  // Update_Changeset: ["Update_Codesheets"],
  // Delete_Changeset: ["Update_Codesheets"],
  // Submit_Changeset: ["Update_Codesheets"],
  Review_Changeset: ["View_Codesheets"],
  Merge_Changeset: ["Update_Codesheets"],
  View_Version_History: ["View_Codesheets"],
  // Compare_Versions: ["View_Version_History"],
  Rollback_Version: ["Update_Codesheets", "View_Version_History"],
};

const normalizePermission = (permission: string): string => LEGACY_PERMISSION_ALIASES[permission] || permission;

const normalizePermissions = (values: string[]): string[] => Array.from(
  new Set((Array.isArray(values) ? values : []).map(normalizePermission))
);


export default function GroupDetailsDrawer(props: GroupDetailsDrawerProps) {
  const { open, onClose, groupId, onSaved } = props;

  const isCreate = !groupId;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [explicitPermissions, setExplicitPermissions] = useState<string[]>([]); // Track user-selected permissions
  const canSave = name.trim().length > 0 && !saving && !loading;

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setName("");
      setDescription("");
      setPermissions([]);
      setExplicitPermissions([]);
      setError(null);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchRoleById(groupId!);
        if (!alive) return;
        setName(data.name || "");
        setDescription(data.description || "");
        const loadedPermissions = normalizePermissions(Array.isArray(data.permissions) ? data.permissions : []);
        setPermissions(loadedPermissions);
        setExplicitPermissions(loadedPermissions); // Assume all loaded permissions are explicit
      } catch (e: any) {
        if (!alive) return;
        setError(e?.response?.data?.message || "Failed to load role");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, groupId, isCreate]);

  // Get required permissions for a given permission
  const getRequiredPermissions = (permission: string): string[] => {
    const visited = new Set<string>();

    const collectDependencies = (currentPermission: string): string[] => {
      const normalizedPermission = normalizePermission(currentPermission);
      if (visited.has(normalizedPermission)) {
        return [];
      }

      visited.add(normalizedPermission);

      const directDependencies = PERMISSION_DEPENDENCIES[normalizedPermission] || [];
      const allDependencies = new Set<string>();

      directDependencies.forEach((dependency) => {
        allDependencies.add(dependency);
        collectDependencies(dependency).forEach((nestedDependency) => allDependencies.add(nestedDependency));
      });

      return Array.from(allDependencies);
    };

    return collectDependencies(permission);
  };

  // Get permissions that depend on a given permission
  const getDependentPermissions = (permission: string): string[] => {
    return Object.values(PERMISSION_GROUPS)
      .flatMap(group => group.permissions)
      .filter(perm => getRequiredPermissions(perm).includes(permission));
  };

  // Recalculate all permissions based on explicit permissions
  const recalculatePermissions = (explicitPerms: string[]): string[] => {
    const normalizedExplicitPerms = normalizePermissions(explicitPerms);
    const allPerms = new Set(normalizedExplicitPerms);
    normalizedExplicitPerms.forEach(perm => {
      getRequiredPermissions(perm).forEach(required => allPerms.add(required));
    });
    return Array.from(allPerms);
  };

  const togglePermission = (value: string) => {
    setExplicitPermissions(prev => {
      const isCurrentlyExplicit = prev.includes(value);

      if (isCurrentlyExplicit) {
        // When deselecting explicit permission, remove it and all permissions that depend on it
        const dependentPerms = getDependentPermissions(value);
        const newExplicit = prev.filter(x => x !== value && !dependentPerms.includes(x));
        // Recalculate permissions (this will automatically remove dependencies if needed)
        setPermissions(recalculatePermissions(newExplicit));
        return newExplicit;
      } else {
        // When selecting, add to explicit permissions
        const newExplicit = normalizePermissions([...prev, value]);
        // Recalculate permissions (this will add required dependencies)
        setPermissions(recalculatePermissions(newExplicit));
        return newExplicit;
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const normalizedPermissions = normalizePermissions(permissions);
      if (isCreate) {
        const payload: CreateRolePayload = { name, description, permissions: normalizedPermissions, id: "", createdAt: null, updatedAt: null };
        await createRole(payload);
      } else {
        const payload: UpdateRolePayload = { name, description, permissions: normalizedPermissions, id: groupId!, createdAt: null, updatedAt: null };
        await updateRole(groupId!, payload);
      }
      if (typeof onSaved === 'function') onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={isCreate ? 'New Role' : 'Edit Role'}
      error={error}
      width="520px"
      footerRight={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave}>{saving ? 'Saving…' : 'Save changes'}</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div>
          <Typography variant="subtitle2" className="font-semibold text-gray-900 mb-2 text-sm">Role details</Typography>
          <div className="space-y-8">
            <TextField
              size="small"
              fullWidth
              label="Role name"
              placeholder="Set the role name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              size="small"
              fullWidth
              label="Description"
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <Typography variant="subtitle2" className="font-semibold text-gray-900 mb-4 text-sm">Permissions</Typography>
          <div className="space-y-6">
            {Object.entries(PERMISSION_GROUPS).map(([key, group]) => (
              <div key={key}>
                <Typography variant="subtitle2" className="font-medium text-gray-800 mb-2 text-sm">{group.title}</Typography>
                <div className="grid grid-cols-1 gap-1.5 ml-2">
                  {group.permissions.map(value => {
                    const isExplicit = explicitPermissions.includes(value);
                    const isAutoIncluded = permissions.includes(value) && !isExplicit;

                    return (
                      <FormControlLabel
                        key={value}
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={permissions.includes(value)}
                            onChange={() => togglePermission(value)}
                            disabled={loading || isAutoIncluded}
                          />
                        }
                        label={
                          <span className={`text-sm ${isAutoIncluded ? 'text-gray-500' : 'text-gray-700'}`}>
                            {formatPermission(value)}
                            {isAutoIncluded && (
                              <span className="text-xs text-orange-600 ml-1">(automatically included)</span>
                            )}
                          </span>
                        }
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DrawerForm>
  );
}


